"use client"

import { motion } from "framer-motion"
import { useInView } from "react-intersection-observer"
import { GraduationCap, Code, Award, Rocket } from "lucide-react"

const timelineEvents = [
    {
        year: "2025",
        title: "LofiHaven",
        description: "Launched a full-featured lofi music platform with focus sessions, user profiles, and a Discord bot integration.",
        icon: Rocket,
        color: "from-purple-500 to-pink-500",
    },
    {
        year: "2024",
        title: "Aether Ticket Bot",
        description: "Built a Discord ticket bot system with a web dashboard for server management. Now discontinued.",
        icon: Award,
        color: "from-blue-500 to-cyan-500",
    },
    {
        year: "2023",
        title: "Akira Discord Bot",
        description: "Created a multi-purpose Discord bot with 400+ commands. A major learning milestone. Now discontinued.",
        icon: Code,
        color: "from-green-500 to-emerald-500",
    },
    {
        year: "2021",
        title: "Started Coding",
        description: "Began my programming journey, exploring JavaScript and building small projects.",
        icon: GraduationCap,
        color: "from-yellow-500 to-amber-500",
    },
]

export function ExperienceTimeline() {
    const [ref, inView] = useInView({
        triggerOnce: true,
        threshold: 0.1,
    })

    return (
        <section id="experience" aria-labelledby="experience-heading" className="py-32 relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-b from-background via-primary/5 to-background" />

            <div className="container mx-auto px-4 relative z-10">
                <motion.div ref={ref} className="max-w-4xl mx-auto">
                    {/* Section header */}
                    <div className="text-center mb-20 space-y-4">
                        <motion.span
                            initial={{ opacity: 0, y: 20 }}
                            animate={inView ? { opacity: 1, y: 0 } : {}}
                            transition={{ delay: 0.2 }}
                            className="inline-block text-sm font-medium px-4 py-1.5 rounded-full bg-muted/10 border border-border text-foreground backdrop-blur-sm"
                        >
                            My Journey
                        </motion.span>
                        <motion.h2
                            initial={{ opacity: 0, y: 20 }}
                            animate={inView ? { opacity: 1, y: 0 } : {}}
                            transition={{ delay: 0.3 }}
                            className="text-4xl md:text-6xl font-bold tracking-tight"
                        >
                            <span id="experience-heading">Experience</span>
                        </motion.h2>
                        <motion.div
                            initial={{ width: 0 }}
                            whileInView={{ width: 100 }}
                            className="h-1 bg-primary rounded-full mx-auto"
                        />
                    </div>

                    {/* Timeline */}
                    <div className="relative">
                        {/* Center line */}
                        <motion.div
                            initial={{ height: 0 }}
                            animate={inView ? { height: "100%" } : {}}
                            transition={{ duration: 1.5, ease: "easeOut" }}
                            className="absolute left-1/2 top-0 w-px bg-gradient-to-b from-primary via-primary/50 to-transparent -translate-x-1/2"
                        />

                        {timelineEvents.map((event, index) => (
                            <motion.div
                                key={event.year}
                                initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                                animate={inView ? { opacity: 1, x: 0 } : {}}
                                transition={{ delay: 0.3 + index * 0.15, duration: 0.5 }}
                                className={`relative flex items-center mb-12 ${index % 2 === 0 ? "flex-row" : "flex-row-reverse"
                                    }`}
                            >
                                {/* Content */}
                                <div className={`w-5/12 ${index % 2 === 0 ? "text-right pr-8" : "text-left pl-8"}`}>
                                    <motion.div
                                        whileHover={{ scale: 1.02 }}
                                        className="p-6 rounded-2xl glass-panel hover:border-primary/50 transition-all"
                                    >
                                        <span className="text-sm font-bold text-primary">{event.year}</span>
                                        <h3 className="text-xl font-bold mt-2 mb-2">{event.title}</h3>
                                        <p className="text-sm text-muted-foreground">{event.description}</p>
                                    </motion.div>
                                </div>

                                {/* Center icon */}
                                <div className="absolute left-1/2 -translate-x-1/2 z-10">
                                    <motion.div
                                        initial={{ scale: 0 }}
                                        animate={inView ? { scale: 1 } : {}}
                                        transition={{ delay: 0.5 + index * 0.15, type: "spring" }}
                                        className={`w-12 h-12 rounded-full bg-gradient-to-br ${event.color} flex items-center justify-center shadow-lg`}
                                    >
                                        <event.icon className="w-5 h-5 text-white" aria-hidden="true" />
                                    </motion.div>
                                </div>

                                {/* Empty space on other side */}
                                <div className="w-5/12" />
                            </motion.div>
                        ))}
                    </div>
                </motion.div>
            </div>
        </section>
    )
}
